import React, { useState } from 'react';
import { motion } from 'motion/react';
import classNames from 'classnames';
import { 
  Brain, 
  Clock, 
  MessageSquare, 
  TrendingUp, 
  BarChart3, 
  Shield,
  Check,
  ArrowRight,
  Zap,
  Users,
  DollarSign,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Squares } from '../components/ui/squares-background';
import { testimonials, faqs, features, howItWorks, stats } from '../mockData';

const iconMap = {
  Brain,
  Clock,
  MessageSquare,
  TrendingUp,
  BarChart3,
  Shield
};

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - will be replaced with backend API
    console.log('Demo booking submitted:', formData);
    alert('Thank you! We\'ll be in touch shortly to schedule your demo.');
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <motion.div 
              className="logo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_deadleads-to-sales/artifacts/s6qls219_Asset%203.svg" 
                alt="Sondr Logo" 
                className="logo-image"
              />
            </motion.div>
            
            {/* Desktop Navigation */}
            <nav className="nav-desktop">
              <a href="#how-it-works" className="nav-link">How It Works</a>
              <a href="#features" className="nav-link">Features</a>
              <a href="#testimonials" className="nav-link">Testimonials</a>
              <a href="#faq" className="nav-link">FAQ</a>
            </nav>

            <div className="header-actions">
              <Button 
                variant="outline" 
                onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary hidden-mobile"
              >
                Book a Demo
              </Button>
              
              <button 
                className="menu-toggle"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <motion.nav 
              className="nav-mobile"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <a href="#how-it-works" className="nav-link-mobile" onClick={() => setIsMenuOpen(false)}>How It Works</a>
              <a href="#features" className="nav-link-mobile" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="#testimonials" className="nav-link-mobile" onClick={() => setIsMenuOpen(false)}>Testimonials</a>
              <a href="#faq" className="nav-link-mobile" onClick={() => setIsMenuOpen(false)}>FAQ</a>
              <Button 
                onClick={() => {
                  setIsMenuOpen(false);
                  document.getElementById('demo').scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full mt-4"
              >
                Book a Demo
              </Button>
            </motion.nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        {/* Animated Grid Background */}
        <div className="hero-background">
          <Squares 
            direction="diagonal"
            speed={0.3}
            squareSize={60}
            borderColor="rgba(151, 225, 57, 0.08)"
            hoverFillColor="rgba(151, 225, 57, 0.05)"
          />
        </div>
        <div className="container">
          <div className="hero-content">
            <motion.div 
              className="hero-text"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.1 }}
            >
              <h1 className="hero-title">
                Turn Your Dead Leads Into Sales
                <span className="hero-title-accent"> With AI & SMS</span>
              </h1>
              <p className="hero-subtitle">
                Performance-based lead reactivation. You only pay when we convert your cold leads into warm conversations. No risk, all reward.
              </p>
              <div className="hero-cta">
                <Button 
                  size="lg"
                  onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-primary btn-large"
                >
                  Get Started Free
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                  className="btn-secondary btn-large"
                >
                  See How It Works
                </Button>
              </div>

              {/* Stats Row */}
              <div className="hero-stats">
                <div className="stat-item">
                  <Zap size={20} className="stat-icon" />
                  <span className="stat-text">67% Reactivation Rate</span>
                </div>
                <div className="stat-item">
                  <Users size={20} className="stat-icon" />
                  <span className="stat-text">500+ Happy Clients</span>
                </div>
                <div className="stat-item">
                  <DollarSign size={20} className="stat-icon" />
                  <span className="stat-text">$2.4M+ Revenue Recovered</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="hero-image"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springConfig, delay: 0.3 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_deadleads-to-sales/artifacts/y6a9yitb_Phone6.png"
                alt="Sondr SMS conversation on mobile"
                className="hero-img"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div 
                key={stat.id}
                className="stat-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
              >
                <h3 className="stat-value">{stat.value}</h3>
                <p className="stat-label">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Four simple steps to turn your cold leads into revenue
            </p>
          </motion.div>

          <div className="how-it-works-grid">
            {howItWorks.map((item, index) => (
              <motion.div 
                key={item.id}
                className="how-it-works-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.15 }}
              >
                <div className="step-number">{item.step}</div>
                <h3 className="step-title">{item.title}</h3>
                <p className="step-description">{item.description}</p>
                {index < howItWorks.length - 1 && (
                  <div className="step-connector">
                    <ArrowRight size={24} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="how-it-works-image">
            <motion.img 
              src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxhdXRvbWF0aW9ufGVufDB8fHx8MTc2MDQ5NDE5MXww&ixlib=rb-4.1.0&q=85"
              alt="AI automation process"
              className="process-image"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.2 }}
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        {/* Animated Grid Background */}
        <div className="features-background">
          <Squares 
            direction="up"
            speed={0.2}
            squareSize={50}
            borderColor="rgba(151, 225, 57, 0.06)"
            hoverFillColor="rgba(151, 225, 57, 0.03)"
          />
        </div>
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="section-title">Powerful Features That Drive Results</h2>
            <p className="section-subtitle">
              Everything you need to reactivate leads and maximize conversions
            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <motion.div 
                  key={feature.id}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ ...springConfig, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="feature-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Performance Model Section */}
      <section className="performance-section">
        <div className="container">
          <div className="performance-content">
            <motion.div 
              className="performance-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <h2 className="performance-title">Pay Only For Results</h2>
              <p className="performance-subtitle">
                Our performance-based model means you take zero risk. We only get paid when you get results.
              </p>
              
              <div className="performance-benefits">
                <div className="benefit-item">
                  <Check className="benefit-icon" size={20} />
                  <span>No upfront costs or monthly retainers</span>
                </div>
                <div className="benefit-item">
                  <Check className="benefit-icon" size={20} />
                  <span>Pay only when leads convert to conversations</span>
                </div>
                <div className="benefit-item">
                  <Check className="benefit-icon" size={20} />
                  <span>Transparent pricing with full analytics</span>
                </div>
                <div className="benefit-item">
                  <Check className="benefit-icon" size={20} />
                  <span>No long-term contracts required</span>
                </div>
              </div>

              <Button 
                size="lg"
                onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                className="btn-primary btn-large mt-6"
              >
                Get Started
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </motion.div>

            <motion.div 
              className="performance-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <img 
                src="https://images.unsplash.com/photo-1716840646010-e5622fd6683d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGxlYWRzJTIwY29udmVyc2lvbnxlbnwwfHx8fDE3NjA0OTQxODZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Performance metrics"
                className="performance-img"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-subtitle">
              Real results from real businesses
            </p>
          </motion.div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={testimonial.id}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.15 }}
              >
                <p className="testimonial-content">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="author-image"
                  />
                  <div className="author-info">
                    <p className="author-name">{testimonial.name}</p>
                    <p className="author-role">{testimonial.role}</p>
                  </div>
                </div>
                <div className="testimonial-stats">{testimonial.stats}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Everything you need to know about Sondr
            </p>
          </motion.div>

          <motion.div 
            className="faq-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                  <AccordionTrigger className="faq-question">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Demo Booking Section */}
      <section id="demo" className="demo-section">
        <div className="container">
          <div className="demo-content">
            <motion.div 
              className="demo-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <h2 className="demo-title">Ready to Turn Dead Leads Into Revenue?</h2>
              <p className="demo-subtitle">
                Book a free consultation and discover how much revenue is hiding in your cold leads.
              </p>
              <div className="demo-benefits">
                <div className="demo-benefit-item">
                  <Check className="check-icon" size={20} />
                  <span>Free lead analysis worth $500</span>
                </div>
                <div className="demo-benefit-item">
                  <Check className="check-icon" size={20} />
                  <span>Custom strategy session</span>
                </div>
                <div className="demo-benefit-item">
                  <Check className="check-icon" size={20} />
                  <span>No commitment required</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="demo-form-container"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <form onSubmit={handleSubmit} className="demo-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <Input 
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Work Email *</label>
                  <Input 
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company Name *</label>
                  <Input 
                    id="company"
                    name="company"
                    type="text"
                    placeholder="Your Company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <Input 
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Tell us about your lead challenge</label>
                  <Textarea 
                    id="message"
                    name="message"
                    placeholder="We have approximately X dead leads from..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows={4}
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  className="btn-primary btn-large w-full"
                >
                  Book Your Free Demo
                  <ArrowRight className="ml-2" size={20} />
                </Button>

                <p className="form-note">
                  We'll respond within 24 hours to schedule your consultation.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src="https://customer-assets.emergentagent.com/job_deadleads-to-sales/artifacts/s6qls219_Asset%203.svg" 
                alt="Sondr Logo" 
                className="footer-logo-image"
              />
              <p className="footer-tagline">
                Performance-based AI lead reactivation.
                <br />You win, we win.
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-section">
                <h4 className="footer-heading">Product</h4>
                <a href="#how-it-works" className="footer-link">How It Works</a>
                <a href="#features" className="footer-link">Features</a>
                <a href="#testimonials" className="footer-link">Testimonials</a>
                <a href="#faq" className="footer-link">FAQ</a>
              </div>

              <div className="footer-section">
                <h4 className="footer-heading">Company</h4>
                <a href="#" className="footer-link">About Us</a>
                <a href="#" className="footer-link">Contact</a>
                <a href="#" className="footer-link">Careers</a>
                <a href="#" className="footer-link">Blog</a>
              </div>

              <div className="footer-section">
                <h4 className="footer-heading">Legal</h4>
                <a href="#" className="footer-link">Privacy Policy</a>
                <a href="#" className="footer-link">Terms of Service</a>
                <a href="#" className="footer-link">Cookie Policy</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} Sondr. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;